import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ReactFlow, Background, Controls, Handle, MarkerType, Position,
  type Edge, type Node, type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import {
  Save, PlusCircle, Camera, ShieldCheck, Flag, IndianRupee, Replace,
  ScanText, Brain, Sparkles, PaintBucket, Calculator, CircuitBoard,
  MessageSquare, ChevronDown, ChevronRight, Maximize2, X,
} from "lucide-react";
import { cls } from "../../lib/cls";
import { ProjectActivityFeed } from "./ProjectActivityFeed";
import { AgenticContextFlow } from "./AgenticContextFlow";
import { ProjectAgentHero } from "./ProjectAgentHero";
// CompetitorTable lives in `docs/competitive-landscape.md` — pitch
// material, not designer surface. Do not re-import here.

/* ──────────────────────────────────────────────────────────────────
   AgenticFlow — designer + client view.
   The buyer is a senior interior designer; their *clients* see the
   same surface when they open a project. Every label here is
   designer-facing. No model names, no framework names, no code
   paths. Internal machinery lives behind admin-only toggles.
   ────────────────────────────────────────────────────────────────── */

interface Signal {
  id: string;
  label: string;
  detail: string;
  icon: JSX.Element;
}

const SIGNALS: Signal[] = [
  { id: "onSave",       label: "You save the project",     detail: "scene + items flush",      icon: <Save className="w-3.5 h-3.5" /> },
  { id: "onSku",        label: "You add an item",          detail: "BOQ row · catalog pick",   icon: <PlusCircle className="w-3.5 h-3.5" /> },
  { id: "onRender",     label: "You click Take Render",    detail: "pre-render check",         icon: <Camera className="w-3.5 h-3.5" /> },
  { id: "onApproval",   label: "Client approval updates",  detail: "decided · aged",           icon: <ShieldCheck className="w-3.5 h-3.5" /> },
  { id: "onMilestone",  label: "You shift a milestone",    detail: "set · moved",              icon: <Flag className="w-3.5 h-3.5" /> },
  { id: "onBudget",     label: "Budget cap changes",       detail: "cap · share moved",        icon: <IndianRupee className="w-3.5 h-3.5" /> },
  { id: "onSubstitute", label: "You accept a substitute",  detail: "accepted · rejected",      icon: <Replace className="w-3.5 h-3.5" /> },
];

interface Stage {
  id: string;
  title: string;
  forDesigner: string;
  icon: JSX.Element;
  example: string;
  /** Per-kind ring colour token. */
  tint: "cream" | "sea" | "foyr" | "fern" | "ink";
}

const STAGES: Stage[] = [
  { id: "parse",  title: "Reads your files",        forDesigner: "Pulls rooms, items, and dimensions out of your floor plan, BOQ, and PDF quotes.", icon: <ScanText className="w-5 h-5" />,       example: "“Living: 240 sq ft · ceiling 10.5 ft · 4 placements”",                          tint: "cream" },
  { id: "ner",    title: "Knows what's what",       forDesigner: "Tags every item — sofa, chandelier, quartz — and every name — vendor, designer, client.",  icon: <PaintBucket className="w-5 h-5" />,    example: "“Sofa F-1042 · vendor NorthCraft”",                                          tint: "cream" },
  { id: "embed",  title: "Understands the vibe",    forDesigner: "Reads mood boards and briefs so it knows what your client means by “warm minimal”.",       icon: <Sparkles className="w-5 h-5" />,       example: "“Intent · Scandinavian-Indo (0.91 match)”",                                  tint: "sea"   },
  { id: "reason", title: "Connects the dots",       forDesigner: "Sees how rooms relate to intent, milestones, budget, vendor SLAs — across the whole project.", icon: <Brain className="w-5 h-5" />,        example: "“Chandelier L-887 ties lighting to vendor v2”",                              tint: "foyr"  },
  { id: "solve",  title: "Checks the limits",       forDesigner: "Spatial, budget, lead time, style cohesion — runs your studio's invariants every save.",   icon: <Calculator className="w-5 h-5" />,     example: "“Kitchen budget 22% · target ≤ 25% — OK”",                                   tint: "cream" },
  { id: "decide", title: "Fires your rules",        forDesigner: "Your library — 25 senior-designer rules + your studio's Memory Cards — fires automatically.", icon: <CircuitBoard className="w-5 h-5" />,  example: "“Lead-time rule fires · proposes Pendant L-901 (14 d, 88%)”",                tint: "fern"  },
  { id: "why",    title: "Tells you (and the client) why", forDesigner: "Plain-English explanation of every alert, cost change, substitute — ready to share.",  icon: <MessageSquare className="w-5 h-5" />, example: "“Substituted because lead-time risked your 18 Jul milestone.”",              tint: "foyr"  },
];

const TINT_RING: Record<Stage["tint"], string> = {
  cream: "ring-border bg-cream-dark text-ink-950",
  sea:   "ring-sea/40 bg-sea/10 text-sea",
  foyr:  "ring-foyr/40 bg-foyr/10 text-foyr-deep",
  fern:  "ring-fern/40 bg-fern/10 text-fern",
  ink:   "ring-ink-950/60 bg-ink-950 text-cream",
};

function isActive(stageId: string, hint: string | undefined): boolean {
  if (!hint) return false;
  switch (stageId) {
    case "parse":  return hint === "ingest";
    case "ner":    return hint === "ingest" || hint === "approvalAged";
    case "embed":  return hint === "ingest" || hint === "styleDrift" || hint === "memoryPromote";
    case "reason": return hint === "ingest" || hint === "explain";
    case "solve":  return hint === "budgetSlip" || hint === "approvalAged";
    case "decide": return hint === "ruleFire" || hint === "alert" || hint === "memoryMatch" || hint === "memoryPromote" || hint === "substitute";
    case "why":    return hint === "explain" || hint === "substitute";
    default:       return false;
  }
}

/** Legacy helper kept for scenario-player wiring outside this module. */
export function statusByAgent(hint: string | undefined): Partial<Record<string, "idle" | "running" | "done">> | undefined {
  if (!hint) return undefined;
  const m: Partial<Record<string, "running">> = {};
  switch (hint) {
    case "ingest":         m["watch"] = "running"; m["ingest"] = "running"; break;
    case "ruleFire":       m["constraint"] = "running"; break;
    case "alert":          m["constraint"] = "running"; m["narrator"] = "running"; break;
    case "memoryMatch":    m["memory"] = "running"; break;
    case "substitute":     m["substitute"] = "running"; m["narrator"] = "running"; break;
    case "explain":        m["narrator"] = "running"; break;
    case "budgetSlip":     m["constraint"] = "running"; break;
    case "styleDrift":     m["constraint"] = "running"; break;
    case "approvalAged":   m["constraint"] = "running"; m["narrator"] = "running"; break;
    case "memoryPromote":  m["memory"] = "running"; break;
  }
  return m;
}

interface AgenticFlowProps {
  /** A step kind from the scenario player to light up a stage. */
  activeStageHint?: string | undefined;
}

export function AgenticFlow({ activeStageHint }: AgenticFlowProps): JSX.Element {
  return (
    <div className="space-y-4">
      <ProjectAgentHero />
      <ProjectActivityFeed activeStageHint={activeStageHint} />
      <AgenticContextFlow />
    </div>
  );
}

/* ─── Header ───────────────────────────────────────────────────── */

/** Legacy generic header. ProjectAgentHero replaced it on the cockpit Agent tab. */
export function DesignerHeader(): JSX.Element {
  return (
    <div className="panel-hero p-5 relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-foyr/15 blur-3xl pointer-events-none" />
      <div className="relative flex items-start gap-3">
        <span className="w-9 h-9 rounded-xl bg-ink-950 text-cream grid place-items-center shrink-0">
          <Brain className="w-4 h-4" />
        </span>
        <div>
          <div className="display-3 text-ink-950 leading-tight">The agent works while you design.</div>
          <p className="mt-1 text-[13px] text-ink-700 max-w-2xl leading-snug">
            Every time you save, add an item, click Take Render, set a milestone, or accept a substitute —
            the agent picks up the call, runs the checks a senior designer would run, and writes the
            result back so you can see it without leaving the canvas.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─── Outcomes ─────────────────────────────────────────────────── */

/** Legacy generic outcome chips. ProjectAgentHero shows live, project-derived metrics instead. */
export function DesignerOutcomes(): JSX.Element {
  const outcomes = [
    { icon: <Camera className="w-3.5 h-3.5" />,         title: "Fewer wasted renders",  detail: "Pre-render style score catches mismatches before you spend a credit." },
    { icon: <IndianRupee className="w-3.5 h-3.5" />,    title: "No budget surprises",   detail: "Per-room budget shares re-balance the moment the cap moves." },
    { icon: <Flag className="w-3.5 h-3.5" />,           title: "No milestone slips",    detail: "Long-lead items are caught at add-time, with an automatic substitute on hand." },
    { icon: <MessageSquare className="w-3.5 h-3.5" />,  title: "Clients hear the why",  detail: "Every cost / delay / swap comes with a one-paragraph reason you can forward." },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
      {outcomes.map((o) => (
        <div key={o.title} className="panel p-3">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-cream-dark border border-border grid place-items-center text-ink-950">{o.icon}</span>
            <span className="text-sm font-semibold text-ink-950 leading-tight">{o.title}</span>
          </div>
          <p className="text-[11px] text-ink-500 mt-1 leading-snug">{o.detail}</p>
        </div>
      ))}
    </div>
  );
}

/* ─── Agent flow diagram — xyflow + dagre ──────────────────────── */

interface SignalNodeData extends Record<string, unknown> {
  label: string;
  detail: string;
  iconKey: string;
}
interface StageNodeData extends Record<string, unknown> {
  title: string;
  body: string;
  example: string;
  iconKey: string;
  tint: Stage["tint"];
  active: boolean;
}

const SIGNAL_ICON: Record<string, JSX.Element> = {
  Save: <Save className="w-4 h-4" />,
  PlusCircle: <PlusCircle className="w-4 h-4" />,
  Camera: <Camera className="w-4 h-4" />,
  ShieldCheck: <ShieldCheck className="w-4 h-4" />,
  Flag: <Flag className="w-4 h-4" />,
  IndianRupee: <IndianRupee className="w-4 h-4" />,
  Replace: <Replace className="w-4 h-4" />,
};
const STAGE_ICON: Record<string, JSX.Element> = {
  ScanText: <ScanText className="w-5 h-5" />,
  PaintBucket: <PaintBucket className="w-5 h-5" />,
  Sparkles: <Sparkles className="w-5 h-5" />,
  Brain: <Brain className="w-5 h-5" />,
  Calculator: <Calculator className="w-5 h-5" />,
  CircuitBoard: <CircuitBoard className="w-5 h-5" />,
  MessageSquare: <MessageSquare className="w-5 h-5" />,
};

function SignalFlowNode({ data }: { data: SignalNodeData }): JSX.Element {
  return (
    <div className="group">
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-cream ring-1 ring-border shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all group-hover:-translate-y-0.5 group-hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.10)]" style={{ minWidth: 200 }}>
        <span className="w-7 h-7 rounded-lg bg-cream-dark border border-border grid place-items-center text-ink-700 shrink-0">
          {SIGNAL_ICON[data.iconKey] ?? <Save className="w-4 h-4" />}
        </span>
        <div className="min-w-0">
          <div className="text-[12px] font-medium text-ink-950 leading-tight truncate">{data.label}</div>
          <div className="text-[10px] text-ink-500 leading-tight truncate">{data.detail}</div>
        </div>
      </div>
    </div>
  );
}

function StageFlowNode({ data }: { data: StageNodeData }): JSX.Element {
  return (
    <div className="group">
      <Handle type="target" position={Position.Left}  className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <div
        className={cls(
          "rounded-2xl bg-cream ring-1 transition-all px-3 pt-3 pb-2.5 text-center cursor-default",
          "shadow-[0_1px_2px_rgba(0,0,0,0.04)] group-hover:-translate-y-0.5 group-hover:shadow-[0_4px_14px_-2px_rgba(0,0,0,0.10)]",
          data.active ? "!ring-2 !ring-foyr shadow-[0_6px_18px_-4px_rgba(201,92,26,0.25)]" : "ring-border",
        )}
        style={{ width: 220 }}
      >
        <div className={cls("mx-auto w-12 h-12 rounded-full ring-2 grid place-items-center mb-2", TINT_RING[data.tint])}>
          {STAGE_ICON[data.iconKey] ?? <Sparkles className="w-5 h-5" />}
        </div>
        <div className="font-newyork text-[13px] font-semibold text-ink-950 leading-tight">{data.title}</div>
        <div className="text-[10.5px] text-ink-500 leading-snug mt-1 px-0.5">{data.body}</div>
        <div className="text-[10.5px] italic text-ink-700 leading-snug mt-1.5">{data.example}</div>
      </div>
    </div>
  );
}

const flowNodeTypes: NodeTypes = {
  signal: SignalFlowNode as unknown as NodeTypes[string],
  stage:  StageFlowNode  as unknown as NodeTypes[string],
};

function iconKeyForSignal(id: string): string {
  switch (id) {
    case "onSave":       return "Save";
    case "onSku":        return "PlusCircle";
    case "onRender":     return "Camera";
    case "onApproval":   return "ShieldCheck";
    case "onMilestone":  return "Flag";
    case "onBudget":     return "IndianRupee";
    case "onSubstitute": return "Replace";
    default:             return "Save";
  }
}
function iconKeyForStage(id: string): string {
  switch (id) {
    case "parse":  return "ScanText";
    case "ner":    return "PaintBucket";
    case "embed":  return "Sparkles";
    case "reason": return "Brain";
    case "solve":  return "Calculator";
    case "decide": return "CircuitBoard";
    case "why":    return "MessageSquare";
    default:       return "Sparkles";
  }
}

function layoutAgentFlow(signals: Signal[], stages: Stage[], activeHint: string | undefined): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "LR", nodesep: 14, ranksep: 110, marginx: 30, marginy: 20 });
  g.setDefaultEdgeLabel(() => ({}));

  const SIGNAL_W = 220, SIGNAL_H = 56;
  const STAGE_W  = 240, STAGE_H  = 168;

  for (const s of signals) g.setNode(s.id, { width: SIGNAL_W, height: SIGNAL_H });
  for (const st of stages) g.setNode(st.id, { width: STAGE_W,  height: STAGE_H });

  // Every signal feeds the "parse" stage; the agent thinks linearly
  // from there. dagre lays each stage on its own rank, signals stack
  // vertically on the leftmost rank.
  for (const s of signals) g.setEdge(s.id, stages[0]!.id);
  for (let i = 0; i < stages.length - 1; i++) {
    g.setEdge(stages[i]!.id, stages[i + 1]!.id);
  }
  dagre.layout(g);

  const nodes: Node[] = [];
  for (const s of signals) {
    const p = g.node(s.id);
    nodes.push({
      id: s.id,
      type: "signal",
      position: { x: (p?.x ?? 0) - SIGNAL_W / 2, y: (p?.y ?? 0) - SIGNAL_H / 2 },
      data: { label: s.label, detail: s.detail, iconKey: iconKeyForSignal(s.id) } as SignalNodeData,
      draggable: false,
      selectable: false,
    });
  }
  for (const st of stages) {
    const p = g.node(st.id);
    nodes.push({
      id: st.id,
      type: "stage",
      position: { x: (p?.x ?? 0) - STAGE_W / 2, y: (p?.y ?? 0) - STAGE_H / 2 },
      data: {
        title: st.title, body: st.forDesigner, example: st.example,
        iconKey: iconKeyForStage(st.id), tint: st.tint,
        active: isActive(st.id, activeHint),
      } as StageNodeData,
      draggable: false,
      selectable: false,
    });
  }

  const edges: Edge[] = [];
  for (const s of signals) {
    edges.push({
      id: `e-${s.id}-${stages[0]!.id}`,
      source: s.id, target: stages[0]!.id,
      type: "smoothstep",
      style: { stroke: "#94908a", strokeWidth: 1 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#94908a", width: 12, height: 12 },
    });
  }
  for (let i = 0; i < stages.length - 1; i++) {
    const causal = isActive(stages[i + 1]!.id, activeHint);
    edges.push({
      id: `e-${stages[i]!.id}-${stages[i + 1]!.id}`,
      source: stages[i]!.id,
      target: stages[i + 1]!.id,
      type: "smoothstep",
      animated: causal,
      style: { stroke: causal ? "#C95C1A" : "#7a766f", strokeWidth: causal ? 1.6 : 1.2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: causal ? "#C95C1A" : "#7a766f", width: 14, height: 14 },
    });
  }
  return { nodes, edges };
}

/** Legacy step-by-step diagram. Retained for the /agent route. */
export function AgentFlowDiagram({ activeStageHint }: { activeStageHint: string | undefined }): JSX.Element {
  const { nodes, edges } = useMemo(
    () => layoutAgentFlow(SIGNALS, STAGES, activeStageHint),
    [activeStageHint],
  );
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [h, setH] = useState(640);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    function r(): void {
      if (!wrapRef.current) return;
      const w = wrapRef.current.clientWidth;
      setH(Math.min(820, Math.max(560, Math.round(w / 1.55))));
    }
    r();
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setExpanded(false); }
    if (expanded) {
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }
    return () => { /* no-op when not expanded */ };
  }, [expanded]);

  const flow = (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={flowNodeTypes}
      fitView
      fitViewOptions={{ padding: 0.16 }}
      minZoom={0.4}
      maxZoom={1.8}
      panOnDrag
      zoomOnScroll={false}
      zoomOnPinch
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      proOptions={{ hideAttribution: true }}
    >
      <Background gap={22} size={1} color="#ece7dc" />
      {expanded ? <Controls showInteractive={false} className="!shadow-sm" /> : null}
    </ReactFlow>
  );

  return (
    <>
      <div ref={wrapRef} className="relative rounded-2xl border border-border/60 overflow-hidden bg-[radial-gradient(circle_at_20%_30%,rgba(201,92,26,0.05),transparent_70%)]" style={{ height: h }}>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label="Expand"
          className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-cream/95 border border-border text-[11px] font-medium text-ink-700 hover:text-ink-950 backdrop-blur-sm shadow-sm transition"
        >
          <Maximize2 className="w-3 h-3" /> Expand
        </button>
        {flow}
      </div>

      {expanded ? createPortal((
        <AnimatePresence>
          <motion.div
            key="bg"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[80] bg-ink-950/40 backdrop-blur-sm"
            onClick={() => setExpanded(false)}
          />
          <motion.div
            key="card"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-4 sm:inset-8 z-[81] panel overflow-hidden flex flex-col"
          >
            <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border/60">
              <div>
                <div className="eyebrow">Agent flow · expanded</div>
                <div className="display-3 text-ink-950 leading-tight">What the agent does, step by step</div>
              </div>
              <button onClick={() => setExpanded(false)} aria-label="Close" className="w-8 h-8 rounded-lg bg-cream-dark border border-border grid place-items-center text-ink-700 hover:text-ink-950 transition">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="flex-1 bg-[radial-gradient(circle_at_20%_30%,rgba(201,92,26,0.05),transparent_70%)]">
              {flow}
            </div>
          </motion.div>
        </AnimatePresence>
      ), document.body) : null}
    </>
  );
}

/* ─── How the agent thinks (was: ReAct loop) ──────────────────── */

interface ThinkStep {
  id: string;
  kind: "think" | "act" | "observe";
  text: string;
}

const THINK_TRACE: ThinkStep[] = [
  { id: "r1", kind: "think",   text: "A 92-day chandelier on the BOQ might miss the 18 Jul lighting milestone." },
  { id: "r2", kind: "act",     text: "Look up substitutes — chandeliers in stock within 21 days, ≥ 85% style match." },
  { id: "r3", kind: "observe", text: "Pendant L-901 · 14-day lead · 88% style match · ΔPrice +₹7,000." },
  { id: "r4", kind: "think",   text: "Match is strong. A prior pattern in studio memory supports this (7 prior accepts)." },
  { id: "r5", kind: "act",     text: "Propose substitute (L-887 → L-901) · open client approval packet." },
  { id: "r6", kind: "observe", text: "Designer accepts · client approval queued · activity feed updated." },
  { id: "r7", kind: "think",   text: "Threshold met (5 accepts, 0 rejects). Promote rule to studio memory." },
  { id: "r8", kind: "act",     text: "Promote rule — lead-time guard becomes a studio default." },
];

/** Legacy reasoning-trace panel. Retained for the /agent route. */
export function HowItThinks({ activeStageHint }: { activeStageHint?: string | undefined }): JSX.Element {
  const [open, setOpen] = useState(true);
  return (
    <div className="panel p-4">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-ink-950 text-cream grid place-items-center">
            <Brain className="w-3.5 h-3.5" />
          </span>
          <div className="text-left">
            <div className="text-sm font-semibold text-ink-950 leading-tight">How the agent thinks</div>
            <div className="text-[11px] text-ink-500 leading-tight">
              Notices something → looks something up → reads what it found, the way a senior designer reasons out loud.
            </div>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-ink-500" /> : <ChevronRight className="w-4 h-4 text-ink-500" />}
      </button>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.ol
            key="trace"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 relative border-l border-border pl-5 space-y-2.5 overflow-hidden"
          >
            {THINK_TRACE.map((step, i) => (
              <motion.li
                key={step.id}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.03 }}
                className="relative"
              >
                <span
                  className={cls(
                    "absolute -left-[27px] top-1 w-2.5 h-2.5 rounded-full border-2",
                    step.kind === "think"   ? "border-sea bg-cream" :
                    step.kind === "act"     ? "border-foyr bg-foyr" :
                                              "border-fern bg-cream",
                  )}
                />
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className={cls(
                    "text-[10px] font-mono uppercase tracking-wider",
                    step.kind === "think"   ? "text-sea" :
                    step.kind === "act"     ? "text-foyr-deep" :
                                              "text-fern",
                  )}>
                    {step.kind === "think" ? "notices" : step.kind === "act" ? "looks up" : "reads"}
                  </span>
                  <span className="text-[12px] text-ink-950 leading-snug">{step.text}</span>
                </div>
              </motion.li>
            ))}
            <li className="text-[10px] text-ink-500 italic pl-1 pt-1">
              The loop repeats on every signal from the canvas — the agent never falls behind your save.
              {activeStageHint ? <span className="ml-1 text-foyr-deep">· currently: {activeStageHint}</span> : null}
            </li>
          </motion.ol>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
